import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  IconButton,
  Menu,
  MenuItem,
  Button,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Plus,
  ArrowLeft,
  EllipsisVertical,
  Edit,
  Trash
} from 'lucide-react';
import { styled, useTheme } from '@mui/material/styles';
import { getThemeStyles } from '../../../themes/themeConfig';
import { useTranslation } from 'react-i18next';

// Estilos optimizados para reducir altura
const MobileHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 10px', // Reduced padding
  height: '48px', // Explicit height
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1100,
  backgroundColor: 'transparent',
  overflow: 'hidden',
  '& .MuiTypography-root': {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  },
  ...getThemeStyles('MobileHeader', theme.palette.mode),
}));

const DesktopHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 10px', // Reduced padding
  height: '48px', // Explicit height
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1100,
  ...getThemeStyles('DesktopHeader', theme.palette.mode),
}));

const MenuButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    padding: '6px',
    minWidth: '32px',
    width: '32px',
    height: '32px',
    backgroundColor: 'transparent',
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? 'rgba(255, 255, 255, 0.1)'
        : theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.12)'
          : '#F8F8F8',
    },
    '& svg': {
      width: '18px',
      height: '18px',
    },
  };
});
 
const BlueIconButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    padding: '6px',
    minWidth: '32px',
    width: '32px',
    height: '32px',
    backgroundColor: 'transparent',
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? 'rgba(255, 255, 255, 0.1)'
        : theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.12)'
          : '#F8F8F8',
    },
    '& svg': {
      width: '18px',
      height: '18px',
    },
  };
});

const ActionButton = styled(Button)(({ theme, variant }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const isSecondary = variant === 'secondary';

  return {
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    borderRadius: '16px',
    textTransform: 'none',
    padding: '4px 10px',
    minHeight: '32px',
    fontSize: '12px',
    fontWeight: 600,
    ...(isSecondary
      ? {
          color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
          backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
          border: `1px solid ${
            isWallpaperMode
              ? 'rgba(255, 255, 255, 0.35)'
              : theme.palette.mode === 'dark'
                ? 'rgba(3, 133, 255, 0.5)'
                : 'rgba(3, 133, 255, 0.5)'
          }`,
          '&:hover': {
            backgroundColor: isWallpaperMode
              ? 'rgba(255, 255, 255, 0.12)'
              : theme.palette.mode === 'dark'
                ? 'rgba(3, 133, 255, 0.12)'
                : 'rgba(3, 133, 255, 0.08)',
          },
        }
      : {
          backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.9)' : '#0385FF',
          color: isWallpaperMode ? '#000000' : '#FFFFFF',
          '&:hover': {
            backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.8)' : '#0371db',
          },
        }),
    '& .MuiButton-startIcon': {
      marginRight: 6,
    },
  };
});

const ActionIconButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    color: isWallpaperMode ? '#000000' : '#FFFFFF',
    backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.9)' : '#0385FF',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    padding: '6px',
    minWidth: '32px',
    width: '32px',
    height: '32px',
    borderRadius: '16px',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.8)' : '#0371db',
    },
    '& svg': {
      width: '16px',
      height: '16px',
    },
  };
});
const CategoryChip = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'inline-flex',
    alignItems: 'center',
    height: '18px',
    padding: '0 6px',
    borderRadius: '9px',
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.15)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(3, 133, 255, 0.1)'
        : 'rgba(3, 133, 255, 0.08)'),
    border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.3)' : 'none',
    color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '0.2px',
    textTransform: 'capitalize',
    marginLeft: theme.spacing(0.75),
    flexShrink: 0,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
  };
});

const HeaderMenuButton = styled(IconButton)(({ theme }) => ({
  padding: 2, // Reduced padding
  marginLeft: 6, // Reduced margin
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.08)' 
      : 'rgba(0, 0, 0, 0.05)',
    color: theme.palette.mode === 'dark' ? '#fff' : '#000',
  },
}));


const ChatHeader = ({
  isMobile,
  currentChatId,
  isNewChatView,
  chatTitle,
  isEditingTitle,
  editedTitle,
  currentChatObj,
  toggleSidebar,
  handleTitleEdit,
  handleTitleSave,
  createNewChatHandler,
  setEditedTitle,
  handleDeleteChat,
  isSidebarOpen,
  hideSidebar,
  user,
  isProjectChat,
  onBackToProject,
  onCreateProjectChat
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    if (currentChatId && handleDeleteChat) {
      handleDeleteChat(currentChatId);
    }
  };

  const handleEditClick = () => {
    // Primero cerramos el menú
    handleMenuClose();
   
    // Luego activamos el modo de edición con un pequeño retraso
    setTimeout(() => {
      handleTitleEdit();
    }, 50);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleSave();
    }
    else if (e.key === 'Escape') {
      e.preventDefault();
      setEditedTitle(chatTitle);
      document.activeElement.blur();
    }
  };


  if (isMobile) {
    return (
      <MobileHeader>
        <Box display="flex" alignItems="center" gap={1}>
          <MenuButton onClick={toggleSidebar}>
            <MenuIcon />
          </MenuButton>
          {isProjectChat && (
            <Tooltip title={t('buttons.back')}>
              <ActionIconButton
                onClick={onBackToProject}
                sx={{
                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.12)'
                    : 'transparent',
                  color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF',
                  border: theme.palette.actualMode === 'wallpaper'
                    ? '1px solid rgba(255, 255, 255, 0.35)'
                    : '1px solid rgba(3, 133, 255, 0.5)',
                  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                  '&:hover': {
                    backgroundColor: theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.12)'
                      : 'rgba(3, 133, 255, 0.12)'
                  }
                }}
              >
                <ArrowLeft size={16} />
              </ActionIconButton>
            </Tooltip>
          )}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: '70%',
            overflow: 'hidden',
          }}
        >
          {currentChatId && !isNewChatView ? (
            <Box display="flex" alignItems="center" maxWidth="100%" overflow="hidden">
              {isEditingTitle ? (
                <TextField
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onBlur={handleTitleSave}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  variant="standard"
                  sx={{
                    input: { 
                      textAlign: 'center',
                      fontSize: '15px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      padding: '2px 0',
                    },
                    maxWidth: '100%',
                    '& .MuiInput-underline:before': {
                      borderBottomColor: 'text.primary',
                    },
                    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                      borderBottomColor: 'text.primary',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <IconButton 
                        size="small" 
                        onClick={handleTitleSave}
                        sx={{ padding: '2px' }}
                      >
                        ✓
                      </IconButton>
                    )
                  }}
                />
              ) : (
                <>
                  <Typography
                    variant="h6"
                    noWrap
                    sx={{
                      fontWeight: 600,
                      fontSize: '15px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                    }}
                    onClick={handleTitleEdit}
                  >
                    {chatTitle || 'AGT Chat'}
                  </Typography>

                  {/* Mostrar chip de categoría si es project o si la categoría no es "personal" */}
                  {(currentChatObj?.type === 'project' ||
                    (currentChatObj?.category && currentChatObj.category !== 'personal')) && (
                    <CategoryChip>
                      {currentChatObj.type === 'project'
                        ? t('chat.header.projectChat', { category: currentChatObj.category })
                        : currentChatObj.category}
                    </CategoryChip>
                  )}

                  <HeaderMenuButton
                    aria-label="More options"
                    onClick={handleMenuOpen}
                    size="small"
                  >
                    <EllipsisVertical size={14} />
                  </HeaderMenuButton>
                </>
              )}
            </Box>
          ) : (
            <Typography
              variant="h6"
              noWrap
              sx={{
                maxWidth: '100%',
                fontSize: '15px'
              }}
            >
              {t('chat.header.newChat')}
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          {isProjectChat ? (
            <>
              <Tooltip title={t('projectViews.chats.newProjectChat')}>
                <ActionIconButton
                  onClick={onCreateProjectChat}
                  sx={{
                    backgroundColor: theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.12)'
                      : 'transparent',
                    color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF',
                    border: theme.palette.actualMode === 'wallpaper'
                      ? '1px solid rgba(255, 255, 255, 0.35)'
                      : '1px solid rgba(3, 133, 255, 0.5)',
                    backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                    WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.12)'
                        : 'rgba(3, 133, 255, 0.12)'
                    }
                  }}
                >
                  <Plus />
                </ActionIconButton>
              </Tooltip>
              <Tooltip title={t('chat.header.newChat')}>
                <ActionIconButton onClick={createNewChatHandler}>
                  <Plus />
                </ActionIconButton>
              </Tooltip>
            </>
          ) : (
            <ActionIconButton onClick={createNewChatHandler}>
              <Plus />
            </ActionIconButton>
          )}
        </Box>
        
        <Menu
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={(e) => {
            e.stopPropagation();
            handleEditClick();
          }} dense>
            <Edit size={14} style={{ marginRight: 8 }} />
            {t('chat.header.rename')}
          </MenuItem>
          <MenuItem 
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick();
            }} 
            dense
            sx={{ 
              color: theme.palette.error.main,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 59, 48, 0.1)' 
                  : 'rgba(255, 59, 48, 0.08)'
              }
            }}
          >
            <Trash size={14} style={{ marginRight: 8 }} />
            {t('chat.header.delete')}
          </MenuItem>
        </Menu>
      </MobileHeader>
    );
  }

  return (
    <DesktopHeader>
      <Box width="33.33%" display="flex" justifyContent="flex-start">
        {!hideSidebar && !isSidebarOpen && (
          <>
            <MenuButton onClick={toggleSidebar}>
              <MenuIcon />
            </MenuButton>
            {isProjectChat ? (
              <ActionButton
                variant="secondary"
                size="small"
                startIcon={<ArrowLeft size={14} />}
                onClick={onBackToProject}
                sx={{ ml: 1 }}
              >
                {t('buttons.back')}
              </ActionButton>
            ) : (
              <ActionButton
                size="small"
                startIcon={<Plus size={14} />}
                onClick={createNewChatHandler}
                sx={{ ml: 1 }}
              >
                {t('chat.header.newChat')}
              </ActionButton>
            )}
          </>
        )}
      </Box>

      <Box 
        width="33.33%" 
        display="flex" 
        justifyContent="center" 
        alignItems="center"
        sx={{ 
          maxWidth: '33.33%',
          overflow: 'hidden'
        }}
      >
        {currentChatId && !isNewChatView ? (
          isEditingTitle ? (
            <TextField
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={handleKeyDown}
              autoFocus
              variant="standard"
              sx={{
                input: { 
                  textAlign: 'center',
                  fontSize: '16px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  padding: '2px 0',
                },
                maxWidth: '100%',
                '& .MuiInput-underline:before': {
                  borderBottomColor: 'text.primary',
                },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                  borderBottomColor: 'text.primary',
                },
              }}
              InputProps={{
                endAdornment: (
                  <IconButton 
                    size="small" 
                    onClick={handleTitleSave}
                    sx={{ padding: '2px' }}
                  >
                    ✓
                  </IconButton>
                )
              }}
            />
          ) : (
            <Box display="flex" alignItems="center" maxWidth="100%" overflow="hidden">
              <Typography
                variant="h6"
                noWrap
                sx={{
                  cursor: 'pointer',
                  fontSize: '16px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                  '&:hover': {
                    textDecoration: 'underline',
                    textDecorationStyle: 'dotted',
                    textDecorationColor: 'rgba(128, 128, 128, 0.5)'
                  }
                }}
                onClick={handleTitleEdit}
              >
                {chatTitle}
              </Typography>

              {/* Mostrar chip de categoría si es project o si la categoría no es "personal" */}
              {(currentChatObj?.type === 'project' ||
                (currentChatObj?.category && currentChatObj.category !== 'personal')) && (
                <CategoryChip>
                  {currentChatObj.type === 'project'
                    ? t('chat.header.projectChat', { category: currentChatObj.category })
                    : currentChatObj.category}
                </CategoryChip>
              )}

              <HeaderMenuButton
                aria-label="More options"
                onClick={handleMenuOpen}
                size="small"
              >
                <EllipsisVertical size={14} />
              </HeaderMenuButton>
            </Box>
          )
        ) : (
          <Typography 
            variant="h6" 
            noWrap 
            sx={{ 
              maxWidth: '100%',
              fontSize: '16px'
            }}
          >
            {t('chat.header.newChat')}
          </Typography>
        )}
      </Box>

      <Box width="33.33%" display="flex" justifyContent="flex-end" gap={1}>
        {isProjectChat && (
          <>
            <ActionButton
              variant="secondary"
              size="small"
              startIcon={<Plus size={14} />}
              onClick={onCreateProjectChat}
            >
              {t('projectViews.chats.newProjectChat')}
            </ActionButton>
            <ActionButton
              size="small"
              startIcon={<Plus size={14} />}
              onClick={createNewChatHandler}
            >
              {t('chat.header.newChat')}
            </ActionButton>
          </>
        )}
      </Box>
      
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: '10px',
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 5px 15px rgba(0, 0, 0, 0.3)' 
              : '0 5px 15px rgba(0, 0, 0, 0.1)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.05)',
            padding: '4px',
            minWidth: '150px'
          }
        }}
      >
        <MenuItem 
          onClick={(e) => {
            e.stopPropagation();
            handleEditClick();
          }} 
          dense 
          sx={{ borderRadius: '6px' }}
        >
          <Edit size={14} style={{ marginRight: 8 }} />
          {t('chat.header.rename')}
        </MenuItem>
        <MenuItem 
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick();
          }} 
          dense
          sx={{ 
            color: theme.palette.error.main,
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 59, 48, 0.1)' 
                : 'rgba(255, 59, 48, 0.08)'
            }
          }}
        >
          <Trash size={14} style={{ marginRight: 8 }} />
          {t('chat.header.delete')}
        </MenuItem>
      </Menu>
    </DesktopHeader>
  );
};

export default React.memo(ChatHeader);
